import { useRef, type ReactNode, type CSSProperties } from "react";

type Tilt3DProps = {
  children: ReactNode;
  className?: string;
  /** Max rotation in degrees on either axis. */
  max?: number;
};

/**
 * Lightweight 3D hover tilt — no dependencies.
 * Updates CSS custom properties (--rx, --ry, --mx, --my) consumed
 * by the `.tilt-3d` utility in styles.css.
 */
export function Tilt3D({ children, className = "", max = 8 }: Tilt3DProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  function onMove(e: React.MouseEvent<HTMLDivElement>) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width;
    const py = (e.clientY - rect.top) / rect.height;
    const ry = (px - 0.5) * (max * 2);
    const rx = -(py - 0.5) * (max * 2);
    el.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
    el.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
    el.style.setProperty("--mx", `${(px * 100).toFixed(1)}%`);
    el.style.setProperty("--my", `${(py * 100).toFixed(1)}%`);
  }

  function onLeave() {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
  }

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className={`tilt-3d relative ${className}`.trim()}
      style={{ "--rx": "0deg", "--ry": "0deg" } as CSSProperties}
    >
      {children}
    </div>
  );
}